<script lang="ts">
import { defineComponent } from 'vue'
import { uniqueId, throttle } from 'lodash'
import { createPopper, Instance as PopperInstance } from '@popperjs/core'
import VueSelect from '@nextrequestco/vue-select'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'
import { removeMarkup } from './remove-markup'
import NrFormLabel from '../form-elements/nr-form-label.vue'
import omit from 'lodash/omit'

interface SelectOption {
  label?: string
  value?: string | number
  [key: string]: any
}

interface SelectComponent {
  $refs: {
    toggle: HTMLElement
    dropdownMenu: HTMLElement
  }
  getOptionLabel: (option: SelectOption) => string
  selectedValue: Array<object>
}

const COMPONENT_NAME = 'NrSelect'

export default defineComponent({
  name: COMPONENT_NAME,

  components: { VueSelect, NrIconButton, NrFormLabel },

  mounted() {
    this.listboxEl?.addEventListener(
      'scroll',
      this.handleListboxScroll.bind(this)
    )
  },

  beforeDestroy() {
    this.popper?.destroy()
    this.listboxEl?.removeEventListener(
      'scroll',
      this.handleListboxScroll.bind(this)
    )
  },

  computed: {
    attrs() {
      // Omit open and option:created events because we need to intercept and not call them twice
      return omit(this.$attrs, ['onOpen', 'onOption:created'])
    },

    listboxEl(): HTMLElement | undefined {
      return (this.$refs.select as SelectComponent)?.$refs.dropdownMenu
    },

    id(): string {
      return uniqueId(`${COMPONENT_NAME}-`)
    },

    searchClassnames() {
      return [
        'input',
        {
          'is-focused': this.searchFocused
        }
      ]
    },

    sanitizedOptions(): (string | SelectOption)[] {
      return ((this.$attrs.options as (string | SelectOption)[]) || []).map(
        (option) => this.sanitizeOption(option)
      )
    }
  },

  props: {
    hideLabel: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    hasDelete: { type: Boolean, default: false }
  },

  data() {
    return {
      missingRequiredValue: false,
      searchFocused: false,
      searchBlurred: true,
      popper: null as PopperInstance | null
    }
  },

  methods: {
    validateOnFocusOut() {
      const selectedValue = (this.$refs.select as SelectComponent)
        ?.selectedValue
      this.missingRequiredValue =
        this.$attrs?.required && !selectedValue?.length
    },

    validateOnInput(val: Array<any> | object) {
      this.missingRequiredValue =
        this.$attrs?.multiple && Array.isArray(val)
          ? this.$attrs?.required && !val.length
          : this.$attrs?.required && !val
    },

    sanitizeOption(option: string | SelectOption): string | SelectOption {
      const label = (this.$attrs.label as string) || 'label'
      if (typeof option === 'string') {
        return removeMarkup(option)
      } else {
        return {
          ...option,
          [label]: removeMarkup(option[label])
        }
      }
    },

    handleListboxScroll: throttle(function (this: any) {
      const loaderEl = this.listboxEl?.querySelector('.infinite-scroll-trigger')
      if (!loaderEl) {
        return
      }
      const containerY =
        this.listboxEl.getBoundingClientRect().bottom +
        loaderEl.getBoundingClientRect().height
      const triggerY = loaderEl.getBoundingClientRect().top

      if (triggerY <= containerY) {
        this.$emit('load-more')
      }
    }, 400),

    handleSearchFocus() {
      this.searchFocused = true
      this.searchBlurred = false
    },

    handleSearchBlur() {
      this.searchFocused = false
      this.searchBlurred = true
    },

    handleSelectedOptionClick(option: {
      option: SelectOption
      deselect: (option: SelectOption) => void
    }) {
      const deselectedOption = option.option
      option.deselect(deselectedOption)
    },

    parseOptionLabel(option: SelectOption): string {
      return (
        (this.$refs.select as SelectComponent)?.getOptionLabel(option) ??
        'option'
      )
    },

    getOptionKey(option: SelectOption): string | number {
      if (typeof option === 'object' && option.value) {
        return option.value
      } else {
        return JSON.stringify(option)
      }
    },

    onInput(val: any) {
      this.validateOnInput(val)
      this.$emit('input', val)
    },

    onDelete(val: SelectOption) {
      this.$emit('on-delete', val)
    },

    onOptionCreate(newOption: SelectOption) {
      const option = this.sanitizeOption(newOption)
      this.$emit('option:created', option)
    },

    handleOpen() {
      const { width } = (this.$el as HTMLElement).getBoundingClientRect()
      if (this.listboxEl) {
        this.listboxEl.style.width = `${width}px`
      }
      this.popper?.update()
      this.$emit('open')
    },

    withPopper(dropdownList: HTMLElement, component: SelectComponent) {
      this.popper = createPopper(component.$refs.toggle, dropdownList)
      return () => this.popper?.destroy()
    }
  }
})
</script>

<template>
  <div class="nr-select field">
    <NrFormLabel
      :class="[
        'nr-label',
        { 'is-sr-only': hideLabel },
        { 'required-label': attrs?.required }
      ]"
      :id="`${id}__label`"
      :for="id"
    >
      <slot name="label">Select label</slot>
    </NrFormLabel>
    <div class="control">
      <VueSelect
        v-bind="attrs"
        @update:modelValue="onInput"
        @option:created="onOptionCreate"
        @open="handleOpen"
        ref="select"
        :class="searchClassnames"
        :inputId="id"
        :deselectFromDropdown="true"
        :getOptionKey="getOptionKey"
        :options="sanitizedOptions"
        :calculate-position="withPopper"
      >
        <template v-slot:search="search">
          <input
            type="search"
            class="vs__search"
            v-bind="search.attributes"
            v-on="search.events"
            :required="missingRequiredValue"
            :aria-required="attrs?.required"
            @focus="handleSearchFocus"
            @blur="handleSearchBlur"
            @focusout="validateOnFocusOut"
          />
        </template>

        <template v-slot:selected-option="{ ...option }">
          <span
            v-if="$attrs.label"
            :class="`vs__select-option-${option.textColor}`"
            >{{ option[$attrs.label as string] }}</span
          >
          <span v-else :class="`vs__select-option-${option.textColor}`">{{
            option.label
          }}</span>
        </template>

        <template v-slot:option="{ ...option }">
          <div class="nr-select-option-container">
            <div v-if="$attrs.label">{{ option[$attrs.label as string] }}</div>
            <div v-else>{{ option.label }}</div>
            <div v-if="hasDelete" class="nr-button-delete-container">
              <NrIconButton
                class="nr-button-delete"
                variant="minimal-neutral"
                size="small"
                :icon="{ name: 'trash-alt', type: 'regular' }"
                :aria-label="`Delete ${
                  $attrs.label ? option[$attrs.label as string] : option.label
                }`"
                @click.stop="onDelete(option)"
              />
            </div>
          </div>
        </template>

        <template v-slot:selected-option-container="optionContainer">
          <template v-if="optionContainer.multiple">
            <div class="vs__selected tag is-black">
              <p v-if="$attrs.label">
                {{ optionContainer.option[$attrs.label as string] }}
              </p>
              <p v-else>{{ optionContainer.option.label }}</p>
              <NrIconButton
                class="vs__deselect vs__clear"
                variant="minimal-neutral"
                size="small"
                :icon="{ name: 'times', type: 'regular' }"
                :disabled="optionContainer.disabled"
                :aria-label="`Deselect ${parseOptionLabel(
                  optionContainer.option
                )}`"
                @click="handleSelectedOptionClick(optionContainer)"
              />
            </div>
          </template>
        </template>

        <template v-slot:list-footer>
          <li
            v-if="hasMore"
            class="infinite-scroll-trigger vs__dropdown-option"
          >
            Loading more options...
          </li>
        </template>
      </VueSelect>
    </div>
    <p v-if="missingRequiredValue" class="required-field">
      This field is required.
    </p>
  </div>
</template>

<style lang="scss">
@import '@nextrequestco/vue-select/dist/vue-select.css';
@import '../../styles/tokens/tokens.scss';

.nr-select {
  .nr-form-label.required-label::after {
    content: ' *';
    padding-left: $spacing-4;
  }

  .required-field {
    font-size: $font-size-1;
    color: $color-red-100;
    margin-top: $spacing-8;
  }

  .label {
    display: inline-block;
  }

  .vs--multiple {
    height: 100%;
  }

  .vs__dropdown-toggle {
    flex: 1 0 100%;
    border: none;
    border-radius: 0;
    padding: 0;
  }

  .vs__search {
    margin: 0;
    border: none;
    padding: 0;
    text-overflow: ellipsis;
  }

  .vs__clear {
    height: $spacing-24;
    width: $spacing-24;
  }

  .vs__select-option-green {
    color: $color-green-100;
    font-weight: $font-weight-semibold;
  }

  .vs__selected {
    text-overflow: ellipsis;
    max-width: 200px;
    overflow: visible;
    white-space: nowrap;
    margin: $spacing-4;
    padding-right: 0;
  }

  .vs__selected-options {
    overflow: hidden;
  }

  .vs__deselect {
    .nr-icon {
      color: $color-white;
      font-size: $font-size-1;
      margin-left: auto;
    }
    &.nr-button {
      padding: $spacing-6;
    }
    &.vs__clear {
      margin-right: 0;
    }
  }

  .vs__actions {
    padding-top: 0;
  }

  .tag {
    p {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.vs__dropdown-option {
  background-color: $color-white;
  white-space: unset;
  overflow-wrap: break-word;
}

.vs__dropdown-option--selected {
  background-color: $color-gray-30;
  color: $color-gray-100;

  &:hover {
    color: inherit;
  }
}

.nr-button-delete-container {
  margin-left: auto;
}

.nr-select-option-container {
  display: flex;
}

.vs__dropdown-option--highlight {
  background-color: $color-gray-100;
  color: $color-white;
  white-space: unset;

  .nr-button.nr-button-delete {
    color: $color-white;
    &:hover {
      color: $color-white;
      white-space: unset;
    }
  }

  &:hover {
    color: $color-white;
    white-space: unset;
  }
}
</style>
